import { ChangeDetectorRef, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { TournamentService } from '../../../services/tournament.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MatchesComponent } from '../../../components/matches/matches.component';
import { Match } from '../../../models/match';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LogService } from '../../../services/log.service';
import _ from 'lodash';
import { SelectModule } from 'primeng/select';
import { RoundService } from '../../../services/round.service';
import { Phase } from '../../../models/phase';
import { Tournament } from '../../../models/tournament';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { MatchService } from '../../../services/match.service';
import { finalize } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { CommonModule } from '@angular/common';
import { MessageModule } from 'primeng/message';
import { PlayoffService } from '../../../services/playoff.service';
import { ConfirmPopupModule } from 'primeng/confirmpopup';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService } from 'primeng/api';
import { AuthService } from '../../../services/auth.service';

@Component({
  selector: 'app-tournament-detail-rounds',
  imports: [
    ButtonModule,
    CommonModule,
    ConfirmDialogModule,
    ConfirmPopupModule,
    FormsModule,
    MatchesComponent,
    MessageModule,
    SelectModule,
    TooltipModule,
    TranslateModule,
  ],
  providers: [
    ConfirmationService,
  ],
  templateUrl: './tournament-detail-rounds.component.html',
  styleUrl: './tournament-detail-rounds.component.scss',
})
export class TournamentDetailRoundsComponent implements OnInit {
  public allowCreatePlayoffs: boolean = false;
  private readonly destroy$ = inject(DestroyRef);
  public loading: boolean = true;
  public loadingPlayoffs: boolean = false;
  public matchesByGroup: any[] = [];
  public rounds: Phase[] = [];
  public selectedRound: Phase | undefined;
  public tournament: Tournament | undefined;

  constructor(
    public authService: AuthService,
    private cdr: ChangeDetectorRef,
    private confirmationService: ConfirmationService,
    private logService: LogService,
    private matchService: MatchService,
    private playoffService: PlayoffService,
    private roundService: RoundService,
    private router: Router,
    private tournamentService: TournamentService,
    private translate: TranslateService,
  ) { }

  ngOnInit(): void {
    this.tournament = this.tournamentService.tournament();
    this.getRounds();
  }

  private getRounds(): void {
    if(!this.tournament?.id) return;
    const roundUrl = this.router.url.split('/').pop();
    this.roundService.getTournamentRounds(this.tournament!.id!)
      .pipe(
        takeUntilDestroyed(this.destroy$),
        finalize(() => this.loading = false),
      )
      .subscribe({
        next: (res) => {
          this.logService.debug('[Rounds] - getTournamentRounds', res);
          this.rounds = _.orderBy(_.map(_.get(res, 'body.data', []), (round: Phase) => {
            return {...round, name: `${this.translate.instant('field.round')} ${round.roundStage}`};
          }), 'order');
          this.cdr.detectChanges();
          // get matches of selected round (round 1 by default) matches
          this.selectedRound = _.find(this.rounds, { id: roundUrl }) || _.first(this.rounds);
          if(this.selectedRound?.id) this.getMatches(this.selectedRound?.id);
          this.checkAllMatchesFinished();
        },
        error: (err) => {
          this.logService.error('[Rounds] - getTournamentRounds', err, this.translate.instant('error.rounds'));
        }
      });
  }

  private getMatches(roundId: string): void {
    this.matchesByGroup = [];
    this.matchService.getMatches(roundId).pipe(takeUntilDestroyed(this.destroy$)).subscribe({
      next: (res) => {
        this.logService.debug('[Rounds] - getMatches', res);
        this.matchesByGroup = _.orderBy(_.map(_.groupBy(_.get(res, 'body.data.matches'), 'groupId'), (matches: Match[], groupId: string) => {
          return {
            group: {
              groupId, 
              groupName: `${this.translate.instant('field.group')} ${_.get(_.first(matches), 'groupName')}`
            }, 
            matches: _.orderBy(matches, 'tableNumber')
          }
        }), 'group.groupName');
        this.cdr.detectChanges();
        // set url path round id to navigate
        this.router.navigate(['admin', 'tournaments', this.tournament?.id, 'rounds', this.selectedRound?.id]);
      },
      error: (err) => {
        this.logService.error('[Rounds] - getMatches', err, this.translate.instant('error.round-matches'));
      }
    });
  }

  public changeRound(): void {
    if(!this.selectedRound?.id) return;
    this.getMatches(this.selectedRound.id);
  }

  // function to check if tournament status must change from PENDING to IN_PROGRESS
  public checkTournamentStatus(): void {
    if(!this.tournament?.id) return;
    if(this.tournament.status === 'PENDING') {
      this.tournamentService.getTournament(this.tournament.id).subscribe(() => {
        this.cdr.detectChanges();
      });;
    }
  }

  // function to check if all matches have FINISHED status to allow create playoffs
  public checkAllMatchesFinished(): void {
    if(!this.tournament?.id) return;
    this.roundService.checkRoundsFinished(this.tournament.id).pipe(
      takeUntilDestroyed(this.destroy$),
    )
    .subscribe({
      next: (res) => {
        this.logService.debug('[Rounds] - checkRoundsFinished', res);
        this.allowCreatePlayoffs = _.get(res, 'body.data.allMatchesFinished', false) && !_.get(res, 'body.data.playoffsStarted', false);
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.logService.error('[Rounds] - checkRoundsFinished', err);
      }
    });
  }

  public showStandings(): void {
    this.router.navigate(['admin', 'tournaments', this.tournament?.id, 'standing']);
  }

  public createPlayoffs(event: Event): void {
    this.confirmationService.confirm({
      target: event.currentTarget as EventTarget,
      message: this.translate.instant('confirmation.playoffs-create'),
      header: '',
      icon: 'pi pi-info-circle',
      closable: false,
      accept: () => {
        if(!this.tournament?.id) return;
        this.loadingPlayoffs = true;
        this.playoffService.createTournamentPlayoffs(this.tournament.id)
          .pipe(
            takeUntilDestroyed(this.destroy$),
            finalize(() => { 
              this.loadingPlayoffs = false;
              this.cdr.detectChanges();
            })
          )
          .subscribe({
            next: (res) => {
              this.logService.debug('[Playoffs] - createTournamentPlayoffs', res);
              if(this.tournament?.id) this.tournamentService.getTournament(this.tournament.id).subscribe(() => {
                this.router.navigate(['admin', 'tournaments', this.tournament?.id, 'playoffs']);
                this.tournamentService.setTournamentSection('playoffs');
              });
            },
            error: (err) => {
              this.logService.error('[Playoffs] - createTournamentPlayoffs', err, this.translate.instant('error.playoffs-create'));
            }
          });
      },
      reject: () => {}
    });
  }
}
